import React, { Fragment, useCallback, useState } from "react";
import { Alert, Badge, Spinner, Table } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBalanceSheet } from "../../../api/accounting";
import { formatTZS } from "../../../utils/format";
import { formatApiError, runLoad } from "../Common/credoraToast";
import AccountingReportFilters from "./AccountingReportFilters";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import { accountingReportBreadcrumbs } from "./accountingReportShell";
import { useAccountingReportFilters } from "./useAccountingReportFilters";

const StatementSection = ({ section }) => (
  <>
    <tr className="table-light">
      <td colSpan={2}><strong>{section.title}</strong></td>
      <td className="text-end"><strong>{formatTZS(section.total)}</strong></td>
    </tr>
    {section.lines.map((line) => (
      <tr key={line.account_id}>
        <td className="ps-4">{line.account_code}</td>
        <td>{line.account_name}</td>
        <td className="text-end">{formatTZS(line.amount)}</td>
      </tr>
    ))}
  </>
);

const BalanceSheetPage = ({ reportMode = false }) => {
  const crumbs = accountingReportBreadcrumbs({ reportMode, title: "Balance Sheet" });
  const filters = useAccountingReportFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(() => fetchBalanceSheet(filters.reportParams()).then(setData), {
      errorMessage: "Failed to load balance sheet.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load balance sheet.")))
      .finally(() => setLoading(false));
  }, [filters.reportParams]);

  return (
    <Fragment>
      <Breadcrumbs mainTitle={crumbs.mainTitle} parent={crumbs.parent} title={crumbs.title} />
      <div className={`container-fluid${reportMode ? " credora-finance-reports pb-5 mb-3" : ""}`}>
        {error && <Alert color="danger">{error}</Alert>}
        <AccountingReportFilters {...filters} onApply={load} loading={loading} />
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : data ? (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Statement of financial position</h5>
                <small className="text-muted">As of {data.as_of_date}{data.branch_name ? ` · ${data.branch_name}` : ""}</small>
              </div>
              <Badge color={data.is_balanced ? "success" : "warning"}>
                {data.is_balanced ? "Balanced" : "Review totals"}
              </Badge>
            </div>
            <div className="card-body">
              <Table responsive bordered size="sm">
                <thead>
                  <tr><th>Code</th><th>Account</th><th className="text-end">Amount</th></tr>
                </thead>
                <tbody>
                  <StatementSection section={data.assets} />
                  <StatementSection section={data.liabilities} />
                  <StatementSection section={data.equity} />
                  <tr className="table-primary">
                    <td colSpan={2}><strong>Total assets</strong></td>
                    <td className="text-end"><strong>{formatTZS(data.total_assets)}</strong></td>
                  </tr>
                  <tr className="table-primary">
                    <td colSpan={2}><strong>Total liabilities + equity</strong></td>
                    <td className="text-end"><strong>{formatTZS(data.total_liabilities_equity)}</strong></td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
};

export default BalanceSheetPage;
